import * as types from "./mutation-types";
import CacheHelper from "@/cacheHelper";
import dayjs from "dayjs";
import { isEmpty } from "lodash";

const cacheConfig = {
  key: "WIDGET_INTEREST",
  ttl: 60 * 5 * 1000, // 5 mins
};

const cache = new CacheHelper(cacheConfig);

/**
 * All VueX actions should go here.
 * They should check if data exists via the cache, and any api calls should be made to the /flat endpoint by default
 */
export const actions = {
  async getLargestLimit({ rootGetters, commit }) {
    const newsWidgets = rootGetters["widgets/getWidgetsByType"](
      "interests-widget"
    );
    const limits = [];
    let largest = 10;
    if (!isEmpty(newsWidgets)) {
      newsWidgets.forEach((widget) => {
        limits.push(parseInt(widget.props.limit));
      });

      largest = limits.sort((a, b) => b - a)[0];
    }

    commit(types.SET_MAX_QUERY_SIZE, largest);
    return Promise.resolve(largest);
  },
  async clearCache(context) {
    cache.removeItem(cacheConfig.key);
    return Promise.resolve(cacheConfig.key);
  },
  async setLimit({ commit }, limit) {
    await commit(types.SET_LIMIT, limit);
    return Promise.resolve(limit);
  },
  async setFilter({ commit }, filter) {
    await commit(types.SET_FILTER, filter);
    return Promise.resolve(filter);
  },
  /**
   * This needs to get data from the relevant endpoints
   * Run each method seperately and then merge together at the end
   */
  async getFeed({ dispatch, commit, rootState, state }, localOptions) {
    let newsItems = [];
    let eventItems = [];
    let contentItems = [];
    let mergeFeed = [];
    let feed = [];

    // we need to make sure that if the news widget is enabled, we don't double-include these items
    const news = cache.getItem("WIDGET_NEWS");
    // const newsIds = news ? news.map((article) => article.id) : [];
    let newsIds = [];

    // do we have a cache?
    // TODO: currently this will only cache the user feed - we don't really need to put the individual items anywere so they may need removing

    if (cache.existsAndIsValid()) {
      feed = cache.getItem(cacheConfig.key);
      // in case the above news items have been cached already

      feed = feed.filter(
        (item) =>
          !item.self.includes("news/") ||
          (item.self.includes("news/") && !newsIds.includes(item.id))
      );
      commit(types.SET_FEED, feed);

      return Promise.resolve(feed);
    }

    let userGroupIds = [];

    // user groups should come from local storage
    if (localStorage.getItem("mft_groups_getUserInterestGroups")) {
      const localGroups = JSON.parse(localStorage.getItem("mft_groups_getUserInterestGroups"));
      userGroupIds = localGroups.groupIds
    } else {
      // fall back to store
      if (
        rootState.auth.currentUser.interestgroups &&
        rootState.auth.currentUser.interestgroups.length > 0
      ) {
        userGroupIds = rootState.auth.currentUser.interestgroups.map(
          (grp) => grp.id
        );
      }
    }

    let query = {
      sort: [
        {
          created_time: {
            order: "desc",
          },
        },
      ],
      query: {
        bool: {
          must: [
            {
              terms: {
                interestgroups: [...userGroupIds, 13],
              },
            },
            {
              term: {
                status: "approved",
              },
            },
          ],
        },
      },
      size: state.largestQuerySize,
    };

    // needs to query everything, it gets filtered later
    const filterTypes = ["news", "events", "content"];

    if (newsIds && newsIds.length > 0) {
      query.query.bool.must_not = [
        {
          terms: {
            article_id: newsIds,
          },
        },
      ];
    }

    if (state.status !== "WORKING") {
      commit(types.SET_STATUS, "WORKING");

      try {
        if (filterTypes.includes("news")) {
          newsItems = await dispatch(
            "News/query",
            {
              query,
              includeWeb: true,
            },
            { root: true }
          );
        }

        if (filterTypes.includes("content")) {
          contentItems = await dispatch(
            "pages/query",
            {
              query,
            },
            { root: true }
          );
        }

        if (filterTypes.includes("events")) {
          eventItems = await dispatch(
            "Events/query",
            {
              query,
              hidePast: true,
            },
            { root: true }
          );
        }

        // we now need to merge the data together and then sort by timestamp
        mergeFeed = [...newsItems, ...eventItems, ...contentItems];

        feed = mergeFeed
          .map((item) => {
            const sortKey = item.start_date
              ? item.start_date
              : item.created_time;
            const val = dayjs(sortKey).unix();
            return { ...item, sortKey: val };
          })
          .sort((a, b) => b.sortKey - a.sortKey);

        commit(types.SET_FEED, feed);
        cache.setItem(cacheConfig.key, feed, true);
        commit(types.SET_STATUS, "WAITING");
        return Promise.resolve(feed);
      } catch (err) {
        commit(types.SET_STATUS, "WAITING");
        return Promise.resolve(err);
      }
    }
  },
};
